import { useState, useEffect, useCallback, useRef, type FormEvent, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAppContext } from '../context/AppContext';
import type { SetList, Song, ApiError } from '../types';

// ---- Helpers ----

function songTitle(song: Song | undefined, songId: string): string {
  return song?.title || songId.slice(0, 8) + '…';
}

// ---- Sub-components ----

function CreateSetListForm({ onCreate }: { onCreate: (name: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(trimmed);
      setName('');
    } catch (err: unknown) {
      setError((err as ApiError)?.message ?? 'Failed to create set list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="setlist-create-form" onSubmit={handleSubmit}>
      <input
        type="text"
        className="setlist-name-input"
        placeholder="New set list name…"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="New set list name"
        required
      />
      <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
        {busy ? 'Creating…' : 'Create'}
      </button>
      {error && <p className="setlist-inline-error" role="alert">{error}</p>}
    </form>
  );
}

interface SetListSongListProps {
  setList: SetList;
  songs: Song[];
  onRemoveSong: (setListId: string, songId: string) => Promise<void>;
  onReorder: (setListId: string, songIds: string[]) => Promise<void>;
}

function SetListSongList({ setList, songs, onRemoveSong, onReorder }: SetListSongListProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const songMap = new Map(songs.map((s) => [s.songId, s]));

  const handleDragStart = (idx: number) => (e: DragEvent<HTMLLIElement>) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (idx: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIdx(idx);
  };

  const handleDrop = (idx: number) => async (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    setOverIdx(null);
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const ids = [...setList.songIds];
    const [moved] = ids.splice(dragIdx, 1);
    ids.splice(idx, 0, moved);
    setDragIdx(null);
    await onReorder(setList.setListId, ids);
  };

  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const handleRemove = async (songId: string) => {
    setRemovingId(songId);
    try { await onRemoveSong(setList.setListId, songId); }
    finally { setRemovingId(null); }
  };

  if (setList.songIds.length === 0) {
    return <p className="setlist-empty-songs">No songs in this set list. Add songs from the library below.</p>;
  }

  return (
    <ol className="setlist-song-list" role="list">
      {setList.songIds.map((sid, idx) => (
        <li
          key={sid}
          className={`setlist-song-item${dragIdx === idx ? ' dragging' : ''}${overIdx === idx ? ' drag-over' : ''}`}
          draggable
          onDragStart={handleDragStart(idx)}
          onDragOver={handleDragOver(idx)}
          onDrop={handleDrop(idx)}
          onDragEnd={handleDragEnd}
          aria-label={`Song ${idx + 1}: ${songTitle(songMap.get(sid), sid)}`}
        >
          <span className="setlist-song-order">{idx + 1}</span>
          <span className="setlist-song-title">{songTitle(songMap.get(sid), sid)}</span>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => handleRemove(sid)}
            disabled={removingId === sid}
            aria-label={`Remove ${songTitle(songMap.get(sid), sid)} from set list`}
          >
            {removingId === sid ? '…' : '✕'}
          </button>
        </li>
      ))}
    </ol>
  );
}

interface AddSongPanelProps {
  setList: SetList;
  songs: Song[];
  onAdd: (setListId: string, songId: string) => Promise<void>;
}

function AddSongPanel({ setList, songs, onAdd }: AddSongPanelProps) {
  const [addingId, setAddingId] = useState<string | null>(null);
  const [songSearch, setSongSearch] = useState('');
  const available = songs.filter(
    (s) => s.parseStatus === 'completed' && !setList.songIds.includes(s.songId),
  );

  const filtered = available.filter((s) => {
    if (!songSearch.trim()) return true;
    const term = songSearch.trim().toLowerCase();
    return (s.title ?? '').toLowerCase().includes(term) || (s.artist ?? '').toLowerCase().includes(term);
  });

  if (available.length === 0) {
    return <p className="setlist-no-available">No more songs available to add.</p>;
  }

  const handleAdd = async (songId: string) => {
    setAddingId(songId);
    try { await onAdd(setList.setListId, songId); }
    finally { setAddingId(null); }
  };

  return (
    <div className="setlist-add-panel">
      <p className="setlist-add-label">Add a song:</p>
      {available.length > 5 && (
        <input
          type="search"
          className="setlist-song-search"
          placeholder="Filter songs…"
          value={songSearch}
          onChange={(e) => setSongSearch(e.target.value)}
          aria-label="Filter available songs"
        />
      )}
      <ul className="setlist-available-songs" role="list">
        {filtered.map((s) => (
          <li key={s.songId} className="setlist-available-item">
            <span>{s.title || 'Untitled'}</span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => handleAdd(s.songId)}
              disabled={addingId === s.songId}
              aria-label={`Add ${s.title || 'Untitled'} to set list`}
            >
              {addingId === s.songId ? '…' : '+ Add'}
            </button>
          </li>
        ))}
        {filtered.length === 0 && songSearch.trim() && (
          <li className="setlist-no-available">No songs match "{songSearch.trim()}"</li>
        )}
      </ul>
    </div>
  );
}

// ---- Main Component ----

export default function SetListEditor() {
  const { state, dispatch } = useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const hasFetched = useRef(false);

  // Fetch set lists and songs on mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [setLists, songs] = await Promise.all([
          apiClient.getSetLists(),
          apiClient.getSongs(),
        ]);
        if (!cancelled) {
          dispatch({ type: 'SET_SETLISTS', payload: setLists });
          dispatch({ type: 'SET_SONGS', payload: songs });
        }
      } catch (err: unknown) {
        if (!cancelled) setError((err as ApiError)?.message ?? 'Failed to load data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [dispatch]);

  const handleCreate = useCallback(async (name: string) => {
    const created = await apiClient.createSetList(name);
    dispatch({ type: 'ADD_SETLIST', payload: created });
    setExpandedId(created.setListId);
  }, [dispatch]);

  const startRename = useCallback((sl: SetList) => {
    setRenamingId(sl.setListId);
    setRenameValue(sl.name);
    setTimeout(() => renameInputRef.current?.focus(), 0);
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue('');
  }, []);

  const submitRename = useCallback(async (sl: SetList) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === sl.name) { cancelRename(); return; }
    setRenameBusy(true);
    try {
      const updated = await apiClient.updateSetList(sl.setListId, { name: trimmed });
      dispatch({ type: 'UPDATE_SETLIST', payload: updated });
      setRenamingId(null);
      setRenameValue('');
    } catch (err: unknown) {
      setError((err as ApiError)?.message ?? 'Failed to rename set list.');
    } finally {
      setRenameBusy(false);
    }
  }, [renameValue, cancelRename, dispatch]);

  const handleDelete = useCallback(async (sl: SetList) => {
    if (!window.confirm(`Delete set list "${sl.name}"? This cannot be undone.`)) return;
    setDeletingId(sl.setListId);
    try {
      await apiClient.deleteSetList(sl.setListId);
      dispatch({ type: 'REMOVE_SETLIST', payload: sl.setListId });
      if (expandedId === sl.setListId) setExpandedId(null);
    } catch (err: unknown) {
      setError((err as ApiError)?.message ?? 'Failed to delete set list.');
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, expandedId]);

  const handleAddSong = useCallback(async (setListId: string, songId: string) => {
    const updated = await apiClient.addSongToSetList(setListId, songId);
    dispatch({ type: 'UPDATE_SETLIST', payload: updated });
  }, [dispatch]);

  const handleRemoveSong = useCallback(async (setListId: string, songId: string) => {
    await apiClient.removeSongFromSetList(setListId, songId);
    // Optimistic update
    const sl = state.setLists.find((s) => s.setListId === setListId);
    if (sl) {
      dispatch({
        type: 'UPDATE_SETLIST',
        payload: { ...sl, songIds: sl.songIds.filter((id) => id !== songId) },
      });
    }
  }, [dispatch, state.setLists]);

  const handleReorder = useCallback(async (setListId: string, songIds: string[]) => {
    // Optimistic update
    const sl = state.setLists.find((s) => s.setListId === setListId);
    if (sl) {
      dispatch({ type: 'UPDATE_SETLIST', payload: { ...sl, songIds } });
    }
    try {
      const updated = await apiClient.reorderSetListSongs(setListId, songIds);
      dispatch({ type: 'UPDATE_SETLIST', payload: updated });
    } catch (err: unknown) {
      // Revert on failure
      if (sl) dispatch({ type: 'UPDATE_SETLIST', payload: sl });
      setError((err as ApiError)?.message ?? 'Failed to reorder songs.');
    }
  }, [dispatch, state.setLists]);

  const toggleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="page">
      <h2>Set Lists</h2>

      <CreateSetListForm onCreate={handleCreate} />

      {loading && <p className="loading-spinner">Loading…</p>}
      {error && <p className="setlist-error" role="alert">{error}</p>}

      {!loading && state.setLists.length === 0 && (
        <p className="setlist-empty">No set lists yet. Create one above.</p>
      )}

      {state.setLists.length > 0 && (
        <ul className="setlist-list" role="list">
          {state.setLists.map((sl) => (
            <li key={sl.setListId} className="setlist-card">
              <div className="setlist-card-header">
                <button
                  type="button"
                  className="setlist-expand-btn"
                  onClick={() => toggleExpand(sl.setListId)}
                  aria-expanded={expandedId === sl.setListId}
                  aria-label={`${expandedId === sl.setListId ? 'Collapse' : 'Expand'} ${sl.name}`}
                >
                  <span className={`setlist-chevron${expandedId === sl.setListId ? ' open' : ''}`}>▶</span>
                  {renamingId === sl.setListId ? (
                    <span className="setlist-rename-wrapper" onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={renameInputRef}
                        type="text"
                        className="setlist-rename-input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); submitRename(sl); }
                          if (e.key === 'Escape') cancelRename();
                        }}
                        onBlur={() => submitRename(sl)}
                        disabled={renameBusy}
                        aria-label="Rename set list"
                      />
                    </span>
                  ) : (
                    <>
                      <span className="setlist-name">{sl.name}</span>
                      <span className="setlist-count">{sl.songIds.length} song{sl.songIds.length !== 1 ? 's' : ''}</span>
                    </>
                  )}
                </button>
                {renamingId !== sl.setListId && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => startRename(sl)}
                    aria-label={`Rename set list ${sl.name}`}
                  >
                    Rename
                  </button>
                )}
                {sl.songIds.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate(`/performance/setlist/${sl.setListId}`)}
                    aria-label={`Perform set list ${sl.name}`}
                  >
                    Perform
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(sl)}
                  disabled={deletingId === sl.setListId}
                  aria-label={`Delete set list ${sl.name}`}
                >
                  {deletingId === sl.setListId ? '…' : 'Delete'}
                </button>
              </div>

              {expandedId === sl.setListId && (
                <div className="setlist-card-body">
                  <SetListSongList
                    setList={sl}
                    songs={state.songs}
                    onRemoveSong={handleRemoveSong}
                    onReorder={handleReorder}
                  />
                  <AddSongPanel
                    setList={sl}
                    songs={state.songs}
                    onAdd={handleAddSong}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
